import React from "react";
import { Box, Text, useColorModeValue } from "@chakra-ui/react";
import type { NoteEntityType } from "../../types/notes";
import Card from "../Card/Card";
import CardBody from "../Card/CardBody";
import CardHeader from "../Card/CardHeader";
import { NotesListEditor } from "./NotesListEditor";
import { useNotesList } from "./useNotesList";

/** Simulation notes — report notes use `ReportNotesHubPanel` / `ReportTabNotesSection`. */
export function NotesPanel(props: {
  entityType: Extract<NoteEntityType, "backtest">;
  entityId: string;
  title?: string;
  subtitle?: string;
}): React.ReactElement {
  const toastBorder = useColorModeValue("gray.200", "gray.600");
  const subtleText = useColorModeValue("gray.500", "gray.400");
  const textColor = useColorModeValue("gray.700", "gray.200");

  const headerTitle = props.title || "Notes";
  const headerSubtitle = props.subtitle ?? `Attach markdown notes to this ${props.entityType}.`;

  const notes = useNotesList({ entityType: props.entityType, entityId: props.entityId });

  return (
    <Card borderRadius="12px">
      <CardHeader pb={2}>
        <Box>
          <Text fontSize="lg" fontWeight="semibold" color={textColor}>
            {headerTitle}
          </Text>
          <Text fontSize="sm" color={subtleText} mt={1}>
            {headerSubtitle}
          </Text>
        </Box>
      </CardHeader>
      <CardBody pt={0}>
        <NotesListEditor
          notes={notes.notes}
          loading={notes.loading}
          error={notes.error}
          draft={notes.draft}
          onDraftChange={notes.setDraft}
          editingId={notes.editingId}
          editingBody={notes.editingBody}
          onEditingBodyChange={notes.setEditingBody}
          saving={notes.saving}
          canSubmit={notes.canSubmit}
          onCreate={() => void notes.onCreate()}
          onStartEdit={notes.startEdit}
          onCancelEdit={notes.cancelEdit}
          onSaveEdit={() => void notes.saveEdit()}
          onDelete={(id) => void notes.onDelete(id)}
          borderColor={toastBorder}
          subtleText={subtleText}
          textColor={textColor}
          borderRadius="12px"
        />
      </CardBody>
    </Card>
  );
}
